// Proposal Document Generator v3.0 — Decision-Driven Engineering Proposal
// Professional oil & gas tender-grade document: confident, decisive, execution-ready
// 13-Section Structure: Cover → Executive Summary → Understanding (+ Key Risks) →
// Technical Approach → Equipment → Risk & Mitigation → HSE → Execution Readiness →
// Mobilization → Assumptions → Exclusions → Company Profile → Call to Action

import { RFQState, SERVICE_TYPES, COUNTRY_OPTIONS } from './rfqConfig';
import { RuleEngineOutput, EquipmentItem } from './ruleEngine';
import { ClientAdjustments } from './ReviewAdjustStep';
import { ValidationReport } from './engineeringValidator';
import { generateProposalContactSection } from './multiChannelContactEngine';

// ─── TYPES ───────────────────────────────────────────────────────────────────

export interface ProposalSection {
  id: string;
  number: number;
  title: string;
  content: ProposalContent;
}

export type ProposalContent =
  | { type: 'cover'; data: CoverPageData }
  | { type: 'text'; paragraphs: string[] }
  | { type: 'table'; headers: string[]; rows: string[][] }
  | { type: 'grouped-equipment'; groups: EquipmentGroup[] }
  | { type: 'list'; items: ListItem[] }
  | { type: 'conditions-table'; rows: ConditionRow[] }
  | { type: 'validation-summary'; report: ValidationReport }
  | { type: 'adjustments'; items: AdjustmentDisplayItem[] }
  | { type: 'next-steps'; steps: NextStep[] };

export interface CoverPageData {
  title: string;
  service: string;
  client: string;
  company: string;
  location: string;
  date: string;
  referenceId: string;
}

export interface EquipmentGroup {
  system: string;
  items: { equipment: string; specification: string }[];
}

export interface ListItem {
  text: string;
  type?: 'standard' | 'warning' | 'info';
}

export interface ConditionRow {
  parameter: string;
  value: string;
  unit: string;
  implication: string;
}

export interface AdjustmentDisplayItem {
  equipment: string;
  action: string;
  status: 'Accepted' | 'Conditional' | 'Rejected';
  note: string;
}

export interface NextStep {
  step: number;
  action: string;
  timeline: string;
}

export interface ProposalDocument {
  sections: ProposalSection[];
  metadata: {
    referenceId: string;
    generatedAt: string;
    version: string;
  };
}

// ─── HELPERS ─────────────────────────────────────────────────────────────────

function getServiceLabel(id: string): string {
  return SERVICE_TYPES.find(s => s.id === id)?.label || id.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

function getCountryLabel(id: string): string {
  return COUNTRY_OPTIONS.find(c => c.id === id)?.label || id;
}

function getPressureRating(psi: number): string {
  if (psi >= 10000) return '10,000 PSI';
  if (psi >= 5000) return '5,000 PSI';
  if (psi >= 3000) return '3,000 PSI';
  if (psi >= 1440) return '1,440 PSI';
  return 'Standard';
}

function getLocationDisplay(rfqState: RFQState): string {
  const country = getCountryLabel(rfqState.country);
  if (!country || country === rfqState.country) return 'To be confirmed';
  const env = rfqState.environment === 'offshore' ? 'Offshore' : rfqState.environment === 'onshore' ? 'Onshore' : '';
  return env ? `${country} — ${env}` : country;
}

// ─── MAIN GENERATOR ──────────────────────────────────────────────────────────

export function generateProposalDocument(
  rfqState: RFQState,
  ruleOutput: RuleEngineOutput,
  clientAdjustments: ClientAdjustments,
  validationReport: ValidationReport | null,
  referenceId: string
): ProposalDocument {
  const serviceLabel = getServiceLabel(rfqState.service);
  const countryLabel = getCountryLabel(rfqState.country);
  const pressure = parseFloat(rfqState.wellheadPressure) || 0;
  const oilRate = parseFloat(rfqState.oilRate) || 0;
  const gasRate = parseFloat(rfqState.gasRate) || 0;
  const pressureRating = getPressureRating(pressure);
  const locationDisplay = getLocationDisplay(rfqState);

  const sections: ProposalSection[] = [];

  // ═══════════════════════════════════════════════════════════════════════════
  // 1. COVER PAGE
  // ═══════════════════════════════════════════════════════════════════════════
  sections.push({
    id: 'cover',
    number: 0,
    title: '',
    content: {
      type: 'cover',
      data: {
        title: 'PRELIMINARY TECHNICAL PROPOSAL',
        service: serviceLabel,
        client: rfqState.fullName || 'Client Representative',
        company: rfqState.companyName || 'Client Organization',
        location: locationDisplay,
        date: new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }),
        referenceId,
      },
    },
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // 2. EXECUTIVE SUMMARY
  // ═══════════════════════════════════════════════════════════════════════════
  sections.push({
    id: 'executive-summary',
    number: 1,
    title: 'Executive Summary',
    content: { type: 'text', paragraphs: buildExecutiveSummary(rfqState, serviceLabel, countryLabel, pressure, pressureRating, oilRate, gasRate, ruleOutput) },
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // 3. UNDERSTANDING OF REQUIREMENT
  // ═══════════════════════════════════════════════════════════════════════════
  sections.push({
    id: 'understanding',
    number: 2,
    title: 'Understanding of Requirement',
    content: { type: 'list', items: buildUnderstanding(rfqState, serviceLabel, locationDisplay, pressure, oilRate, gasRate) },
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // 3B. KEY RISK FACTORS
  // ═══════════════════════════════════════════════════════════════════════════
  sections.push({
    id: 'key-risks',
    number: 3,
    title: 'Key Risk Factors',
    content: { type: 'list', items: buildKeyRiskFactors(rfqState, pressure, gasRate) },
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // 4. PROPOSED TECHNICAL APPROACH
  // ═══════════════════════════════════════════════════════════════════════════
  sections.push({
    id: 'technical-approach',
    number: 4,
    title: 'Proposed Technical Approach',
    content: { type: 'text', paragraphs: buildTechnicalApproach(rfqState, pressure, pressureRating, oilRate, gasRate, ruleOutput) },
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // 5. EQUIPMENT CONFIGURATION
  // ═══════════════════════════════════════════════════════════════════════════
  sections.push({
    id: 'equipment-config',
    number: 5,
    title: 'Equipment Configuration',
    content: { type: 'table', headers: ['System', 'Description & Engineering Basis'], rows: buildEquipmentTable(ruleOutput.equipmentList, rfqState, pressure) },
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // 6. RISK & MITIGATION
  // ═══════════════════════════════════════════════════════════════════════════
  sections.push({
    id: 'risk-mitigation',
    number: 6,
    title: 'Risk Assessment & Mitigation',
    content: { type: 'table', headers: ['Risk Factor', 'Mitigation Strategy'], rows: buildRiskMitigation(rfqState, pressure, gasRate) },
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // 7. HSE & COMPLIANCE
  // ═══════════════════════════════════════════════════════════════════════════
  sections.push({
    id: 'hse-compliance',
    number: 7,
    title: 'HSE & Compliance',
    content: { type: 'list', items: buildHSECompliance(rfqState) },
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // 8. EXECUTION READINESS
  // ═══════════════════════════════════════════════════════════════════════════
  sections.push({
    id: 'execution-readiness',
    number: 8,
    title: 'Execution Readiness',
    content: { type: 'list', items: buildExecutionReadiness(rfqState, ruleOutput) },
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // 9. MOBILIZATION & DEPLOYMENT
  // ═══════════════════════════════════════════════════════════════════════════
  sections.push({
    id: 'mobilization',
    number: 9,
    title: 'Mobilization & Deployment',
    content: { type: 'text', paragraphs: buildMobilization(rfqState) },
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // 10. ASSUMPTIONS & CLARIFICATIONS
  // ═══════════════════════════════════════════════════════════════════════════
  sections.push({
    id: 'assumptions',
    number: 10,
    title: 'Assumptions & Clarifications',
    content: { type: 'list', items: buildAssumptions(rfqState, ruleOutput) },
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // 11. EXCLUSIONS
  // ═══════════════════════════════════════════════════════════════════════════
  sections.push({
    id: 'exclusions',
    number: 11,
    title: 'Exclusions',
    content: { type: 'list', items: buildExclusions(rfqState) },
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // 12. COMPANY PROFILE
  // ═══════════════════════════════════════════════════════════════════════════
  sections.push({
    id: 'company-profile',
    number: 12,
    title: 'Company Profile',
    content: { type: 'text', paragraphs: buildCompanyProfile() },
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // 13. CALL TO ACTION
  // ═══════════════════════════════════════════════════════════════════════════
  sections.push({
    id: 'call-to-action',
    number: 13,
    title: 'Recommendation & Next Steps',
    content: { type: 'next-steps', steps: buildCallToAction(rfqState) },
  });

  return {
    sections,
    metadata: {
      referenceId,
      generatedAt: new Date().toISOString(),
      version: '3.0',
    },
  };
}

// ─── SECTION BUILDERS ────────────────────────────────────────────────────────

function buildExecutiveSummary(
  rfqState: RFQState, service: string, country: string,
  pressure: number, pressureRating: string, oilRate: number, gasRate: number,
  ruleOutput: RuleEngineOutput
): string[] {
  const paras: string[] = [];
  const envText = rfqState.environment === 'offshore' ? 'offshore' : 'onshore';
  const mobTime = rfqState.country === 'ksa' ? '24–48 hours' : '48–72 hours';

  // Decisive opening — no passive language
  paras.push(
    `EnergiPro Solutions will deliver a fully configured ${service} package for ${envText} operations ` +
    `in ${country}. The proposed configuration addresses all stated operating conditions and is ready ` +
    `for deployment within ${mobTime} of scope confirmation.`
  );

  // Operating conditions — specific, direct
  const conditions: string[] = [];
  if (pressure > 0) conditions.push(`${pressureRating} wellhead pressure`);
  if (oilRate > 0) conditions.push(`${oilRate.toLocaleString()} BOPD production rate`);
  if (gasRate > 0) conditions.push(`${gasRate} MMSCFD gas handling`);
  if (rfqState.h2sPresence === 'yes') conditions.push('H₂S sour service environment');
  if (rfqState.sandProduction === 'high' || rfqState.sandProduction === 'medium') conditions.push(`${rfqState.sandProduction} sand loading`);

  if (conditions.length > 0) {
    paras.push(
      `This solution is engineered specifically for: ${conditions.join(', ')}. ` +
      `Every equipment selection in this proposal is driven by these operating parameters — ` +
      `no generic configurations have been applied.`
    );
  }

  // Prioritization & confidence signal
  paras.push(
    `Your request has been prioritized under our engineering workflow. Configuration selected based on ` +
    `field-proven performance under similar operating conditions.`
  );

  // Execution Readiness Block
  paras.push(
    `EXECUTION READINESS: Equipment Availability — Confirmed. Engineering Review — Completed. ` +
    `Mobilization Readiness — Immediate upon approval.`
  );

  // Confidence statement
  paras.push(
    `The equipment specified in this proposal is available, inspected, and certified. Personnel are ` +
    `trained and deployment-ready. EnergiPro operates under a zero-tolerance safety framework ` +
    `with IMS controls aligned to ISO 45001, ISO 14001, and ISO 9001 — delivering 185,000+ manhours ` +
    `without a Lost Time Incident.`
  );

  // Action-driving close
  paras.push(
    `This document contains all technical information required to proceed. EnergiPro recommends ` +
    `a brief technical alignment call to validate parameters, after which formal mobilization ` +
    `commitment can be issued within 24 hours.`
  );

  return paras;
}

function buildUnderstanding(
  rfqState: RFQState, service: string, location: string,
  pressure: number, oilRate: number, gasRate: number
): ListItem[] {
  const items: ListItem[] = [];

  items.push({ text: `Service Required: ${service}`, type: 'standard' });
  items.push({ text: `Location: ${location}`, type: 'standard' });

  if (pressure > 0) {
    items.push({ text: `Wellhead Pressure: ${rfqState.wellheadPressure} PSI`, type: 'standard' });
  }
  if (oilRate > 0) {
    items.push({ text: `Oil Flow Rate: ${oilRate.toLocaleString()} BOPD`, type: 'standard' });
  }
  if (gasRate > 0) {
    items.push({ text: `Gas Flow Rate: ${gasRate} MMSCFD`, type: 'standard' });
  }
  if (rfqState.temperature) {
    items.push({ text: `Flowing Temperature: ${rfqState.temperature}°C`, type: 'standard' });
  }
  items.push({
    text: `H₂S Presence: ${rfqState.h2sPresence === 'yes' ? 'Confirmed — NACE MR0175 sour service configuration activated' : 'Not indicated — standard materials applied'}`,
    type: rfqState.h2sPresence === 'yes' ? 'warning' : 'standard',
  });
  items.push({
    text: `Sand Production: ${rfqState.sandProduction ? rfqState.sandProduction.charAt(0).toUpperCase() + rfqState.sandProduction.slice(1) + (rfqState.sandProduction === 'high' ? ' — dedicated sand management deployed' : rfqState.sandProduction === 'medium' ? ' — filtration included' : '') : 'Not specified'}`,
    type: rfqState.sandProduction === 'high' ? 'warning' : 'standard',
  });

  if (rfqState.duration) {
    const dur = rfqState.duration === 'short-term' ? 'Short-term campaign (14 days)' : 'Extended operations (60 days)';
    items.push({ text: `Duration: ${dur}`, type: 'standard' });
  }
  if (rfqState.serviceModel) {
    const model = rfqState.serviceModel === 'full-service' ? 'Full Service — EnergiPro provides equipment, operators, and supervision' : 'Equipment Rental — client-operated with EnergiPro technical support';
    items.push({ text: `Service Model: ${model}`, type: 'standard' });
  }

  return items;
}

function buildKeyRiskFactors(rfqState: RFQState, pressure: number, gasRate: number): ListItem[] {
  const items: ListItem[] = [];

  if (pressure > 5000) {
    items.push({ text: `HIGH PRESSURE: Wellhead pressure of ${rfqState.wellheadPressure} PSI exceeds 5,000 PSI threshold — 10K PSI equipment class mandatory. All connections, flowlines, and valves rated per API 6A 10K requirements.`, type: 'warning' });
  } else if (pressure > 1440) {
    items.push({ text: `ELEVATED PRESSURE: ${rfqState.wellheadPressure} PSI requires 5K PSI-rated equipment throughout the testing spread. Standard 1440 PSI equipment is not adequate for this operation.`, type: 'warning' });
  }

  if (rfqState.h2sPresence === 'yes') {
    items.push({ text: 'SOUR SERVICE: H₂S confirmed. Zero-compromise approach — every wetted component verified to NACE MR0175. Continuous gas monitoring deployed. All personnel H₂S-certified and SCBA-equipped before site entry.', type: 'warning' });
  }

  if (rfqState.sandProduction === 'high') {
    items.push({ text: 'HIGH SAND LOADING: Unmanaged sand will cause erosive failure of downstream equipment within hours. Multi-stage desanding system deployed upstream of all process equipment — non-negotiable for this condition.', type: 'warning' });
  } else if (rfqState.sandProduction === 'medium') {
    items.push({ text: 'SAND PRODUCTION: Medium sand loading requires upstream filtration to protect separator internals and measurement accuracy.', type: 'warning' });
  }

  if (gasRate > 30) {
    items.push({ text: `HIGH GAS RATE: ${rfqState.gasRate} MMSCFD exceeds standard flare capacity. Upgraded flare system with thermal radiation compliance per API RP 521 is deployed.`, type: 'warning' });
  }

  if (rfqState.environment === 'offshore') {
    items.push({ text: 'OFFSHORE ENVIRONMENT: Marine-rated equipment mandatory. All lifting operations per LOLER. Deck load and logistics coordination required with vessel operator.', type: 'warning' });
  }

  if (rfqState.locationType === 'remote' || rfqState.locationType === 'desert') {
    items.push({ text: 'REMOTE LOCATION: Self-contained operational capability required. Power generation, logistics fleet, communications, and fuel supply included in deployment package.', type: 'info' });
  }

  if (rfqState.timeline === 'immediate') {
    items.push({ text: 'EXPEDITED TIMELINE: Immediate mobilization requested. Equipment pre-staging and priority personnel assignment initiated upon scope confirmation.', type: 'info' });
  }

  if (items.length === 0) {
    items.push({ text: 'Standard operating conditions identified. No elevated risk factors detected — standard equipment configuration and procedures apply.', type: 'standard' });
  }

  return items;
}

function buildTechnicalApproach(
  rfqState: RFQState, pressure: number, pressureRating: string,
  oilRate: number, gasRate: number, ruleOutput: RuleEngineOutput
): string[] {
  const paras: string[] = [];

  // Pressure handling — decisive
  if (pressure > 0) {
    const pressureClass = pressure > 5000 ? '10K PSI' : pressure > 1440 ? '5K PSI' : '1440 PSI';
    paras.push(
      `Pressure Control: Client wellhead pressure of ${rfqState.wellheadPressure} PSI dictates ` +
      `${pressureClass}-rated equipment throughout the surface spread. The choke manifold (rated ${pressureRating}) ` +
      `controls wellhead pressure down to safe separator operating levels. This is not optional — ` +
      `API 6A pressure class requirements mandate this rating for the stated conditions. All flowlines, ` +
      `connections, and valves match this pressure class with no exceptions.`
    );
  }

  // Separation — engineering-driven
  const sepCapacity = oilRate > 10000 ? 'high-capacity' : oilRate > 5000 ? 'medium-capacity' : 'standard';
  paras.push(
    `Separation & Measurement: A ${sepCapacity} three-phase test separator handles the stated ` +
    `production rates with adequate retention time for accurate phase separation. Instrumentation provides ` +
    `real-time pressure, temperature, and flow data — recorded continuously via data acquisition ` +
    `for reservoir performance evaluation. Separator sizing ensures no overflow conditions at maximum ` +
    `anticipated flow rates.`
  );

  // Sand management — condition-justified
  if (rfqState.sandProduction === 'high' || rfqState.sandProduction === 'medium') {
    const sandText = rfqState.sandProduction === 'high'
      ? `The stated high sand production makes multi-stage solids removal mandatory. A desander, sand filter, and debris catcher are deployed upstream of all process equipment. Without this system, separator internals, instrumentation, and valves face erosive failure within hours of operation.`
      : `Medium sand loading requires upstream filtration to protect separator internals and maintain measurement accuracy. A sand filter is installed upstream of the test separator.`;
    paras.push(`Sand Management: ${sandText}`);
  }

  // Flare — justified by gas rate
  const flareJustification = gasRate > 30
    ? `Gas rate of ${rfqState.gasRate} MMSCFD exceeds standard burner capacity. An upgraded high-capacity flare system is deployed, sized per API RP 521 thermal radiation limits.`
    : `Standard flare system with appropriate burner configuration handles the stated gas volumes safely.`;
  paras.push(
    `Flare & Disposal: ${flareJustification} Flare positioning complies with thermal radiation ` +
    `calculations ensuring safe distances from occupied areas. Produced liquids route to tankage ` +
    `for controlled disposal.`
  );

  // Safety — decisive
  const safetyText = rfqState.h2sPresence === 'yes'
    ? `H₂S is confirmed. This drives the entire safety architecture: NACE MR0175 materials on every wetted surface, continuous gas detection with automatic alarms at 5 ppm and 10 ppm, SCBA at all occupied positions, and defined escape routes. There is zero tolerance for deviation from sour service protocols.`
    : `Standard fire and gas detection with emergency shutdown capability is deployed. All pressure-containing equipment holds current API 6A certification.`;
  paras.push(`Safety Architecture: ${safetyText}`);

  return paras;
}

function buildEquipmentTable(equipment: EquipmentItem[], rfqState: RFQState, pressure: number): string[][] {
  const rows: string[][] = [];
  const pressureRating = getPressureRating(pressure);
  const psi = rfqState.wellheadPressure || 'stated';

  // Choke Manifold — justified
  const chokeItem = equipment.find(e => e.name.toLowerCase().includes('choke'));
  rows.push(['Choke Manifold', `${pressureRating}-rated adjustable choke manifold. Selected because ${psi} PSI wellhead pressure requires this pressure class per API 6A. Provides primary pressure step-down to safe separator operating levels.${chokeItem ? ` Spec: ${chokeItem.specification}` : ''}`]);

  // Separator — justified
  const sepItem = equipment.find(e => e.name.toLowerCase().includes('separator'));
  const oilRate = parseFloat(rfqState.oilRate) || 0;
  const sepReason = oilRate > 10000 ? `High-capacity unit selected for ${oilRate.toLocaleString()} BOPD throughput` : oilRate > 5000 ? `Medium-capacity unit for ${oilRate.toLocaleString()} BOPD` : 'Standard capacity adequate for stated flow rates';
  rows.push(['Test Separator', `Three-phase horizontal test separator with level controls, PSVs, and instrumentation. ${sepReason}. Ensures accurate phase separation and measurement.${sepItem ? ` Spec: ${sepItem.specification}` : ''}`]);

  // Flare — justified
  const flareItem = equipment.find(e => e.name.toLowerCase().includes('flare'));
  const gasRate = parseFloat(rfqState.gasRate) || 0;
  const flareReason = gasRate > 30 ? `Upgraded system — ${gasRate} MMSCFD exceeds standard burner capacity` : 'Standard burner configuration adequate for stated gas volumes';
  rows.push(['Flare System', `Flare boom with burner assembly per API RP 521 thermal radiation requirements. ${flareReason}. Positioned per safety zone calculations.${flareItem ? ` Spec: ${flareItem.specification}` : ''}`]);

  // Flowlines — justified
  const flowItem = equipment.find(e => e.name.toLowerCase().includes('flowline') || e.name.toLowerCase().includes('flow line'));
  rows.push(['Flowlines', `${pressureRating}-rated surface flowlines with hammer unions. Pressure rating matches choke manifold class — no weak links in pressure containment chain.${flowItem ? ` Spec: ${flowItem.specification}` : ''}`]);

  // Instrumentation — justified
  const instrItem = equipment.find(e => e.name.toLowerCase().includes('instrument') || e.name.toLowerCase().includes('data acquisition'));
  rows.push(['Instrumentation', `Pressure, temperature, and flow measurement with continuous data acquisition. Required for accurate reservoir performance evaluation and real-time operational monitoring.${instrItem ? ` Spec: ${instrItem.specification}` : ''}`]);

  // Sand Management — justified by condition
  if (rfqState.sandProduction === 'high' || rfqState.sandProduction === 'medium') {
    const sandItem = equipment.find(e => e.name.toLowerCase().includes('sand') || e.name.toLowerCase().includes('desander'));
    const sandReason = rfqState.sandProduction === 'high'
      ? 'Multi-stage system (desander + filter + debris catcher). Mandatory for high sand — unmanaged solids cause erosive failure of downstream equipment.'
      : 'Sand filtration installed upstream of separator. Required to protect internals and maintain measurement accuracy under medium sand conditions.';
    rows.push(['Sand Management', `${sandReason}${sandItem ? ` Spec: ${sandItem.specification}` : ''}`]);
  }

  // Safety — justified by H2S/conditions
  const safetyReason = rfqState.h2sPresence === 'yes'
    ? 'Full sour service safety package: NACE-compliant ESD, continuous H₂S detection, SCBA units, wind indicators, escape routes. Driven by confirmed H₂S — zero compromise on personnel protection.'
    : 'ESD system, fire & gas detection, safety relief valves. Standard safety package for sweet service operations per API RP 54.';
  rows.push(['Safety System', safetyReason]);

  // Power — justified by location
  const genItem = equipment.find(e => e.name.toLowerCase().includes('generator') || e.name.toLowerCase().includes('power'));
  const powerReason = rfqState.locationType === 'remote' || rfqState.locationType === 'desert'
    ? 'Self-contained diesel generator. Required for remote location — no external power infrastructure available.'
    : 'Power supply arranged per site conditions. Generator included if no reliable site power confirmed.';
  rows.push(['Power Supply', `${powerReason}${genItem ? ` Spec: ${genItem.specification}` : ''}`]);

  return rows;
}

function buildRiskMitigation(rfqState: RFQState, pressure: number, gasRate: number): string[][] {
  const rows: string[][] = [];

  if (rfqState.sandProduction === 'high') {
    rows.push(['High sand production → equipment erosion', 'Multi-stage desanding deployed upstream. Scheduled clean-out intervals. Sand accumulation monitored continuously. Equipment downstream protected.']);
  } else if (rfqState.sandProduction === 'medium') {
    rows.push(['Medium sand production → measurement degradation', 'Sand filtration installed upstream of separator. Regular inspection during operations. Proactive clean-out before accumulation affects performance.']);
  }

  if (rfqState.h2sPresence === 'yes') {
    rows.push(['H₂S exposure → personnel safety hazard', 'NACE MR0175 materials throughout. Continuous gas monitoring (5 ppm alert / 10 ppm evacuate). All personnel H₂S-certified and SCBA-equipped. Defined escape routes and muster points.']);
  }

  if (pressure > 5000) {
    rows.push([`High pressure (${rfqState.wellheadPressure} PSI) → containment risk`, `10K PSI-rated equipment class deployed. No standard-rated components in pressure path. Additional barriers per API RP 14E. Pre-operation pressure test mandatory.`]);
  }

  if (rfqState.locationType === 'remote' || rfqState.locationType === 'desert') {
    rows.push(['Remote location → logistics dependency', 'Self-contained operations package: dedicated power, fuel reserve, communications, and support vehicles. No dependency on external infrastructure.']);
  }

  if (rfqState.environment === 'offshore') {
    rows.push(['Offshore environment → complex logistics', 'Marine-rated equipment. LOLER-certified lifting. Deck load calculations completed. Weather window planning integrated into execution schedule.']);
  }

  if (gasRate > 30) {
    rows.push([`High gas rate (${rfqState.gasRate} MMSCFD) → thermal radiation`, 'Upgraded flare system per API RP 521. Thermal radiation calculations verified. Flare positioned at safe distance from nearest occupied area.']);
  }

  // Always include standard operational risks
  rows.push(['Equipment failure during operations', 'Pre-mobilization function testing per EPI-MNT-003. Spare critical components on-site. 24-hour technical support available. Backup equipment staged at base.']);
  rows.push(['Schedule delay → client impact', 'Equipment confirmed available before commitment. Logistics pre-arranged. Crew on standby. No uncommitted dependencies in execution plan.']);

  return rows;
}

function buildHSECompliance(rfqState: RFQState): ListItem[] {
  const items: ListItem[] = [];

  // Zero-tolerance positioning
  items.push({ text: 'ZERO-TOLERANCE SAFETY POLICY: No operation proceeds without completed JSA, verified equipment certification, and confirmed personnel competency. Stop Work Authority is absolute and extends to every person on site regardless of position.', type: 'warning' });

  items.push({ text: 'IMS-Controlled Execution: Every operation conducted under EnergiPro Integrated Management System. No deviation from established procedures permitted without formal Management of Change approval.', type: 'standard' });
  items.push({ text: 'Risk Assessment: Job Safety Analysis (JSA) completed and signed off before any task commences. Toolbox Talk delivered at every shift start — attendance is mandatory, not optional.', type: 'standard' });
  items.push({ text: 'PPE Compliance: Full PPE enforced at all times in operational zones — hard hat, safety glasses, steel-toe boots, FR coveralls, H₂S monitor (where applicable). Non-compliance results in immediate removal from site.', type: 'standard' });
  items.push({ text: 'Gas Detection: Fixed and portable detection systems deployed. Alarm thresholds set conservatively. Automatic shutdown activated on confirmed gas release.', type: 'standard' });

  if (rfqState.h2sPresence === 'yes') {
    items.push({ text: 'H₂S PROTOCOL (ACTIVATED): All personnel H₂S-certified (OPITO/equivalent) and SCBA-trained before site entry. Continuous monitoring at 5 ppm (Alert) / 10 ppm (Evacuate). SCBA available at every occupied location. Escape routes confirmed and briefed daily. No exceptions.', type: 'warning' });
    items.push({ text: 'NACE MR0175: Every pressure-containing and wetted component verified for sour service. Material certificates documented, traceable, and available for audit.', type: 'warning' });
  }

  items.push({ text: 'Equipment Integrity: Pre-mobilization inspection per EPI-MNT-003 with documented function testing. Third-party certifications verified current. No expired certification deployed under any circumstance.', type: 'standard' });
  items.push({ text: 'Incident Management: All incidents, near-misses, and observations reported within 24 hours. Root cause analysis completed. Corrective actions tracked to closure.', type: 'standard' });
  items.push({ text: 'HSE Performance: TRIR 0.00 | LTIR 0.00 | 185,000+ manhours without Lost Time Incident. This record reflects operational discipline, not luck.', type: 'info' });
  items.push({ text: 'Applicable Standards: API RP 54, API 6A, ASME B31.3, NFPA 30, NACE MR0175, ISO 45001, ISO 14001.', type: 'info' });

  return items;
}

function buildExecutionReadiness(rfqState: RFQState, ruleOutput: RuleEngineOutput): ListItem[] {
  const items: ListItem[] = [];
  const mobTime = rfqState.country === 'ksa' && rfqState.timeline === 'immediate' ? '24–48 hours' : rfqState.timeline === 'immediate' ? '48–72 hours' : '7–14 days';

  items.push({ text: `EQUIPMENT STATUS: All specified equipment is available and staged at Al Khobar base facility. Pre-mobilization inspection can commence immediately upon scope confirmation. Configuration selected based on field-proven performance under similar operating conditions.`, type: 'info' });
  items.push({ text: `PERSONNEL STATUS: Qualified crew (${rfqState.serviceModel === 'full-service' ? 'Supervisor + 2–4 Operators + HSE Officer' : 'Technical support team'}) identified and deployment-ready. All certifications current.`, type: 'info' });
  items.push({ text: `MOBILIZATION WINDOW: ${mobTime} from scope confirmation to equipment on-site. This timeline is confirmed, not estimated.`, type: 'info' });
  items.push({ text: `LOGISTICS: Transport fleet (heavy-haul + crane + support vehicles) pre-arranged with confirmed carriers. No logistics dependency outstanding.`, type: 'standard' });
  items.push({ text: `DOCUMENTATION: Certification packages, operating procedures, and equipment datasheets are compiled and ready for client review or audit.`, type: 'standard' });

  if (rfqState.h2sPresence === 'yes') {
    items.push({ text: 'SOUR SERVICE READINESS: NACE-compliant equipment set segregated and clearly identified. H₂S safety equipment (SCBA, detectors, wind socks) available and function-tested.', type: 'warning' });
  }

  items.push({ text: 'Your request has been prioritized under our engineering workflow. Scope confirmation from client is the only outstanding action item. All EnergiPro-side preparation is complete.', type: 'warning' });

  return items;
}

function buildMobilization(rfqState: RFQState): string[] {
  const paras: string[] = [];

  // Timeline — readiness, not possibility
  let mobTimeline = '48–72 hours';
  if (rfqState.timeline === 'immediate' && rfqState.country === 'ksa') {
    mobTimeline = '24–48 hours';
  } else if (rfqState.timeline === 'immediate') {
    mobTimeline = '48–72 hours';
  } else if (rfqState.timeline === '1-2-weeks') {
    mobTimeline = '7–14 days';
  } else if (rfqState.timeline === 'planned') {
    mobTimeline = 'per agreed schedule';
  }
  paras.push(
    `Deployment Timeline: Equipment arrives on-site within ${mobTimeline} of scope confirmation. ` +
    `This is a confirmed capability based on current equipment availability and pre-arranged logistics — ` +
    `not a conditional estimate.`
  );

  // Crew — readiness confirmed
  if (rfqState.serviceModel === 'full-service') {
    paras.push(
      `Crew Deployment: Senior Field Supervisor (1), Well Test Operators (2–4 based on scope), ` +
      `HSE Officer (1, dedicated full-time). All personnel hold current IADC/IWCF certifications, ` +
      `are H₂S-trained where required, and have completed EnergiPro competency verification. ` +
      `Crew is identified and on standby — deployment authorization is the only outstanding requirement.`
    );
  } else {
    paras.push(
      `Technical Support: Equipment delivered with complete documentation (certifications, operating ` +
      `procedures, maintenance schedules). EnergiPro technical support available 24/7 throughout ` +
      `operations. Rig-up supervision deployed upon request at no additional mobilization delay.`
    );
  }

  // Logistics — confirmed, not arranged
  const logisticsText = rfqState.locationType === 'remote' || rfqState.locationType === 'desert'
    ? 'Dedicated logistics fleet confirmed for remote deployment: heavy-haul transport, crane, support vehicles, fuel supply. All pre-staged and ready for dispatch. No third-party dependency that could delay execution.'
    : 'Heavy-haul transport and crane confirmed with established carriers. Site delivery coordinated to minimize client operational disruption. Equipment layout drawing provided for client approval.';
  paras.push(`Logistics: ${logisticsText}`);

  // Site requirements — clear, not passive
  paras.push(
    `Client Requirements: Access road suitable for heavy transport, level laydown area ` +
    `(minimum 30m × 30m), crane access for rig-up, and wellhead readiness confirmation. ` +
    `EnergiPro provides equipment layout drawing and rig-up procedure prior to mobilization.`
  );

  return paras;
}

function buildAssumptions(rfqState: RFQState, ruleOutput: RuleEngineOutput): ListItem[] {
  const items: ListItem[] = [];

  // Controlled language — not "subject to" but "validated once..."
  items.push({ text: 'Configuration is based on client-provided operating parameters. Final optimization occurs once detailed well data and completion program are received — core equipment selection remains unchanged.', type: 'standard' });
  items.push({ text: 'Equipment is currently available. Availability is locked upon scope confirmation. If specific items are under maintenance at time of confirmation, equivalent certified alternatives are deployed with client notification.', type: 'standard' });
  items.push({ text: 'Site access, crane availability, and laydown area are confirmed by client minimum 48 hours before equipment dispatch. EnergiPro provides site requirements checklist upon scope confirmation.', type: 'standard' });
  items.push({ text: 'All third-party certifications are current and verified. Client-specific inspection requirements (if any) are communicated at scope confirmation stage for pre-mobilization completion.', type: 'standard' });
  items.push({ text: 'Consumables (fuel, water) provided by client unless full-service contract explicitly states otherwise. EnergiPro confirms consumable requirements in pre-mobilization checklist.', type: 'standard' });

  if (rfqState.h2sPresence === 'yes') {
    items.push({ text: 'H₂S concentration levels and safety zone dimensions required from client to finalize gas detection placement and escape route planning. Preliminary layout based on standard H₂S protocol.', type: 'warning' });
  }
  if (rfqState.environment === 'offshore') {
    items.push({ text: 'Vessel/platform deck specifications and crane capacity required from client for lift planning. Equipment weights and dimensions provided upon scope confirmation.', type: 'info' });
  }
  if (ruleOutput.flags.length > 0) {
    items.push({ text: 'Engineering flags identified require client confirmation for final design lock. These do not affect mobilization readiness — they refine operational parameters only.', type: 'info' });
  }

  return items;
}

function buildExclusions(rfqState: RFQState): ListItem[] {
  const items: ListItem[] = [];

  items.push({ text: 'Downhole tools, wireline services, and well intervention equipment', type: 'standard' });
  items.push({ text: 'Civil works, site preparation, and ground construction', type: 'standard' });
  items.push({ text: 'Well control equipment and BOP stack (client-provided)', type: 'standard' });
  items.push({ text: 'Third-party inspection fees beyond EnergiPro standard certification', type: 'standard' });
  items.push({ text: 'Third-party services not explicitly specified in scope', type: 'standard' });
  items.push({ text: 'Site remediation and environmental cleanup post-demobilization', type: 'standard' });
  items.push({ text: 'Consumables (fuel, water, chemicals) unless included in full-service contract', type: 'standard' });

  if (rfqState.environment === 'offshore') {
    items.push({ text: 'Marine vessel/barge hire and deck operations', type: 'standard' });
  }

  items.push({ text: 'Any services or equipment not explicitly stated in this proposal', type: 'standard' });

  return items;
}

function buildCompanyProfile(): string[] {
  return [
    `EnergiPro Solutions delivers integrated oilfield services across Saudi Arabia and the GCC. Operating from Al Khobar, Eastern Province, with regional presence in UAE and Egypt — the company executes well testing, pressure control, engineering, fabrication, and equipment supply for national oil companies, international operators, and Tier-1 EPC contractors.`,
    `Core capabilities: Well Testing & Pressure Control (up to 10K PSI), Stimulation & Nitrogen Services, Custom Fabrication & Skid Assembly, Oilfield Chemicals, Equipment Rental, Filtration & Water Treatment, and Logistics Support.`,
    `Fleet: 120+ equipment units under dedicated maintenance and certification programs. Operations under IMS aligned with ISO 45001, ISO 14001, and ISO 9001. Formal third-party certification targeted Q3–Q4 2026. HSE record: TRIR 0.00, LTIR 0.00, 185,000+ manhours.`,
    `Leadership: 20+ years upstream operational experience across KSA, UAE, Egypt, and MENA. Direct management involvement on every project. 2-hour escalation response. No administrative layers between client and decision-maker.`,
  ];
}

function buildCallToAction(rfqState: RFQState): NextStep[] {
  const mobTimeline = rfqState.timeline === 'immediate' ? '24–72 hours' : rfqState.timeline === '1-2-weeks' ? '7–14 days' : 'Per agreed schedule';

  return [
    { step: 1, action: 'Schedule Technical Alignment Call — validate operating parameters and confirm configuration (30 minutes)', timeline: 'Within 48 hours' },
    { step: 2, action: 'Client issues scope confirmation — EnergiPro locks equipment and initiates pre-mobilization', timeline: 'Immediate upon alignment' },
    { step: 3, action: 'EnergiPro delivers formal commercial proposal with fixed pricing', timeline: '24–48 hours after scope lock' },
    { step: 4, action: 'Contract execution and purchase order', timeline: 'Per client procurement' },
    { step: 5, action: 'Equipment mobilization — on-site and operational', timeline: mobTimeline },
    { step: 6, action: 'THIS PROPOSAL IS EXECUTION-READY. EnergiPro has equipment available, personnel on standby, and logistics confirmed. The only outstanding item is client confirmation. Reply directly, request a call, or share additional data — our engineering team responds within 2 hours.', timeline: 'IMMEDIATE' },
  ];
}

// ─── PDF GENERATION ──────────────────────────────────────────────────────────

export function generateProposalPDF(
  proposal: ProposalDocument,
  rfqState: RFQState,
  ruleOutput: RuleEngineOutput
): void {
  const serviceLabel = getServiceLabel(rfqState.service);
  const countryLabel = getCountryLabel(rfqState.country);

  const printWindow = window.open('', '_blank');
  if (!printWindow) return;

  const htmlContent = buildPDFHTML(proposal, serviceLabel, countryLabel, rfqState, ruleOutput);
  printWindow.document.write(htmlContent);
  printWindow.document.close();
  setTimeout(() => printWindow.print(), 500);
}

function buildPDFHTML(
  proposal: ProposalDocument,
  service: string,
  country: string,
  rfqState: RFQState,
  ruleOutput: RuleEngineOutput
): string {
  const coverData = proposal.sections[0]?.content as { type: 'cover'; data: CoverPageData };

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>EnergiPro Solutions — Preliminary Technical Proposal | ${coverData.data.referenceId}</title>
  <style>
    @page { margin: 20mm 15mm; size: A4; }
    * { box-sizing: border-box; }
    body { font-family: 'Segoe UI', 'Helvetica Neue', Arial, sans-serif; color: #1a1a2e; line-height: 1.7; margin: 0; padding: 0; font-size: 11pt; }

    .cover { page-break-after: always; display: flex; flex-direction: column; justify-content: center; min-height: 90vh; padding: 60px 40px; text-align: center; position: relative; }
    .cover::before { content: ''; position: absolute; top: 0; left: 0; right: 0; height: 6px; background: linear-gradient(90deg, #e8650a, #1a1a2e); }
    .cover-logo { font-size: 28pt; font-weight: 800; color: #0f1729; letter-spacing: 2px; margin-bottom: 8px; }
    .cover-tagline { color: #e8650a; font-size: 10pt; text-transform: uppercase; letter-spacing: 3px; margin-bottom: 50px; }
    .cover-title { font-size: 24pt; font-weight: 700; color: #0f1729; margin-bottom: 6px; border-bottom: 3px solid #e8650a; padding-bottom: 16px; display: inline-block; }
    .cover-service { font-size: 14pt; color: #374151; margin-top: 8px; }
    .cover-meta { margin-top: 50px; }
    .cover-meta table { margin: 0 auto; border-collapse: collapse; }
    .cover-meta td { padding: 8px 20px; text-align: left; font-size: 10pt; }
    .cover-meta td:first-child { color: #666; font-weight: 600; text-transform: uppercase; font-size: 9pt; letter-spacing: 0.5px; }
    .cover-footer { position: absolute; bottom: 40px; left: 0; right: 0; text-align: center; color: #999; font-size: 8pt; }

    .section { page-break-inside: avoid; margin-bottom: 28px; }
    .section-header { display: flex; align-items: center; gap: 12px; margin-bottom: 16px; padding-bottom: 8px; border-bottom: 2px solid #e8650a; }
    .section-num { background: #e8650a; color: white; width: 28px; height: 28px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 12pt; font-weight: 700; flex-shrink: 0; }
    .section-title { font-size: 14pt; font-weight: 700; color: #0f1729; text-transform: uppercase; letter-spacing: 0.5px; }

    p { margin: 0 0 12px; text-align: justify; }

    table { width: 100%; border-collapse: collapse; margin: 12px 0 20px; font-size: 10pt; }
    th { text-align: left; padding: 10px 12px; background: #f0f2f5; border-bottom: 2px solid #d1d5db; font-weight: 700; text-transform: uppercase; font-size: 9pt; letter-spacing: 0.5px; color: #374151; }
    td { padding: 8px 12px; border-bottom: 1px solid #e5e7eb; vertical-align: top; }
    tr:last-child td { border-bottom: 2px solid #d1d5db; }
    td:first-child { font-weight: 600; color: #0f1729; white-space: nowrap; width: 160px; }

    .list-item { padding: 6px 0 6px 16px; position: relative; font-size: 10pt; line-height: 1.6; }
    .list-item::before { content: '▪'; position: absolute; left: 0; color: #e8650a; font-weight: bold; }
    .list-item.warning { background: #fef3cd; padding: 8px 12px 8px 28px; border-radius: 4px; margin: 4px 0; border-left: 3px solid #f59e0b; }
    .list-item.warning::before { content: '⚠'; left: 8px; }
    .list-item.info { background: #eff6ff; padding: 8px 12px 8px 28px; border-radius: 4px; margin: 4px 0; border-left: 3px solid #3b82f6; }
    .list-item.info::before { content: 'ℹ'; left: 8px; color: #3b82f6; }

    .step-row { display: flex; gap: 12px; padding: 10px 0; border-bottom: 1px solid #f0f0f0; align-items: flex-start; }
    .step-row:last-child { border-bottom: none; background: #fef3cd; padding: 14px 12px; border-radius: 6px; margin-top: 8px; }
    .step-num { background: #0f1729; color: white; width: 26px; height: 26px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 10pt; font-weight: 700; flex-shrink: 0; }
    .step-content { flex: 1; }
    .step-action { font-weight: 600; font-size: 10pt; margin-bottom: 2px; }
    .step-timeline { color: #666; font-size: 9pt; }

    .doc-footer { margin-top: 40px; padding-top: 20px; border-top: 2px solid #e5e7eb; color: #666; font-size: 8pt; text-align: center; }
    .doc-footer strong { color: #0f1729; }
  </style>
</head>
<body>
  <div class="cover">
    <div class="cover-logo">ENERGIPRO SOLUTIONS</div>
    <div class="cover-tagline">Integrated Oilfield Equipment & Field Support</div>
    <div class="cover-title">${coverData.data.title}</div>
    <div class="cover-service">${coverData.data.service}</div>
    <div class="cover-meta">
      <table>
        <tr><td>Prepared For</td><td>${coverData.data.client}, ${coverData.data.company}</td></tr>
        <tr><td>Service</td><td>${coverData.data.service}</td></tr>
        <tr><td>Location</td><td>${coverData.data.location}</td></tr>
        <tr><td>Date</td><td>${coverData.data.date}</td></tr>
        <tr><td>Reference</td><td>${coverData.data.referenceId}</td></tr>
      </table>
    </div>
    <div class="cover-footer">CONFIDENTIAL — This document is intended for the named recipient only.</div>
  </div>

  ${proposal.sections.slice(1).map(section => renderPDFSection(section)).join('\n')}

  <div class="doc-footer">
    <p><strong>EnergiPro Solutions</strong> — Al Khobar, Eastern Province, Saudi Arabia</p>
    <p>Reference: ${proposal.metadata.referenceId} | ${new Date(proposal.metadata.generatedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
    <p>This document constitutes a preliminary technical assessment. Formal commercial offer issued upon scope confirmation.</p>
  </div>

  <!-- Contact & Next Steps -->
  <div class="section" style="page-break-before: always;">
    <div class="section-header"><div class="section-num">14</div><div class="section-title">Contact & Communication Channels</div></div>
    <pre style="font-family: 'Segoe UI', Arial, sans-serif; font-size: 10pt; white-space: pre-wrap; line-height: 1.8; color: #1e293b;">${generateProposalContactSection(proposal.metadata.referenceId, service)}</pre>
  </div>
</body>
</html>`;
}

function renderPDFSection(section: ProposalSection): string {
  let html = `<div class="section">`;
  html += `<div class="section-header"><div class="section-num">${section.number}</div><div class="section-title">${section.title}</div></div>`;

  const content = section.content;

  switch (content.type) {
    case 'text':
      html += content.paragraphs.map(p => `<p>${p}</p>`).join('');
      break;

    case 'conditions-table':
      html += `<table><thead><tr><th>Parameter</th><th>Value</th><th>Unit</th><th>Engineering Implication</th></tr></thead><tbody>`;
      html += content.rows.map(r => `<tr><td>${r.parameter}</td><td><strong>${r.value}</strong></td><td>${r.unit}</td><td>${r.implication}</td></tr>`).join('');
      html += `</tbody></table>`;
      break;

    case 'grouped-equipment':
      content.groups.forEach(g => {
        html += `<div style="margin-bottom:12px;"><div style="font-weight:700;font-size:11pt;padding:6px 12px;background:#f8f9fa;border-left:4px solid #e8650a;margin-bottom:6px;">${g.system}</div>`;
        g.items.forEach(item => {
          html += `<div style="padding:4px 12px 4px 24px;font-size:10pt;">${item.equipment} — <span style="color:#666;">${item.specification}</span></div>`;
        });
        html += `</div>`;
      });
      break;

    case 'table':
      html += `<table><thead><tr>${content.headers.map(h => `<th>${h}</th>`).join('')}</tr></thead><tbody>`;
      html += content.rows.map(row => `<tr>${row.map(cell => `<td>${cell}</td>`).join('')}</tr>`).join('');
      html += `</tbody></table>`;
      break;

    case 'list':
      content.items.forEach(item => {
        const cls = item.type === 'warning' ? ' warning' : item.type === 'info' ? ' info' : '';
        html += `<div class="list-item${cls}">${item.text}</div>`;
      });
      break;

    case 'adjustments':
      html += `<table><thead><tr><th>Equipment</th><th>Action</th><th>Status</th><th>Note</th></tr></thead><tbody>`;
      content.items.forEach(item => {
        html += `<tr><td>${item.equipment}</td><td>${item.action}</td><td><strong>${item.status}</strong></td><td>${item.note}</td></tr>`;
      });
      html += `</tbody></table>`;
      break;

    case 'validation-summary':
      const report = content.report;
      html += `<p><strong>Overall Validation Status: ${report.overallStatus}</strong></p>`;
      html += `<p style="font-style:italic;font-size:10pt;">${report.engineeringRecommendation.split('\n')[0]}</p>`;
      break;

    case 'next-steps':
      content.steps.forEach(step => {
        html += `<div class="step-row"><div class="step-num">${step.step}</div><div class="step-content"><div class="step-action">${step.action}</div><div class="step-timeline">${step.timeline}</div></div></div>`;
      });
      break;
  }

  html += `</div>`;
  return html;
}
